/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useShowMessage } from '../../core-ui';
import { toMap } from '../../support/helpers/toMap';
import { useFetchJson } from '../../support/useFetch';
import type { Tag, TagCreateInput } from '../../types/shared/Tag';

import { VendorTagDialog } from './VendorTagDialog/VendorTagDialog';
import { useQueryTags } from './api';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onChangeSaved: () => void;
};

export function TagManagerDialog(props: Props) {
  const { isOpen, onClose, onChangeSaved } = props;
  const showMessage = useShowMessage();
  const fetchJson = useFetchJson();

  const [tags, { refetch }] = useQueryTags({
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    },
  });

  const { mutate: createTag } = useMutation(
    ['POST:/tags'],
    async (newTag: TagCreateInput) => {
      return await fetchJson('/api/tags', { body: newTag });
    },
    {
      onSuccess: () => {
        showMessage('success', t('Tag created successfully'));
        refetch();
        onChangeSaved();
      },
      onError: (error) => {
        showMessage('error', t('Error creating tag: {error}', { error }));
      },
    },
  );

  const { mutate: updateTag } = useMutation(
    ['PUT:/tags/:id'],
    async (tag: Tag) => {
      return await fetchJson(`/api/tags/${tag.id}/edit`, { body: tag });
    },
    {
      onSuccess: () => {
        showMessage('success', t('Tag saved successfully'));
        refetch();
        onChangeSaved();
      },
      onError: (error) => {
        showMessage('error', t('Error saving tag: {error}', { error }));
      },
    },
  );

  const { mutate: deleteTag } = useMutation(
    ['DELETE:/tags/:id'],
    async (tagId: string) => {
      return await fetchJson(`/api/tags/${tagId}/delete`, { body: {} });
    },
    {
      onSuccess: () => {
        showMessage('success', t('Tag deleted successfully'));
        refetch();
        onChangeSaved();
      },
      onError: (error) => {
        showMessage('error', t('Error deleting tag: {error}', { error }));
      },
    },
  );

  const tagMap = useMemo(() => toMap(tags ?? []), [tags]);

  return (
    <VendorTagDialog
      tags={tagMap}
      isOpen={isOpen}
      onHide={onClose}
      onTagCreate={(newTag) => createTag(newTag)}
      onTagEditSave={(tag) => updateTag(tag)}
      onTagDelete={(tagId) => deleteTag(tagId)}
    />
  );
}
